import { useCallback, useState } from 'react';
import { useSwitchChain, useWalletClient } from 'wagmi';
import { chainName, targetChainId } from '../constant/constant';
import {
    getAddEthereumChainParams,
    isChainNotAddedError,
} from '../utils/targetChainWallet';

export function useSwitchToTargetChain() {
    const { switchChainAsync, isPending: isSwitchPending } = useSwitchChain();
    const { data: walletClient } = useWalletClient();
    const [isAddPending, setIsAddPending] = useState(false);
    const [needsAddNetwork, setNeedsAddNetwork] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const displayChainName = chainName || 'Arc';

    const addTargetChain = useCallback(async () => {
        if (!walletClient) {
            const err = new Error(
                'Wallet not ready. Open your wallet extension and try again.',
            );
            setErrorMessage(err.message);
            throw err;
        }

        setIsAddPending(true);
        setErrorMessage('');
        try {
            await walletClient.request({
                method: 'wallet_addEthereumChain',
                params: [getAddEthereumChainParams()],
            });
            setNeedsAddNetwork(false);
        } catch (error) {
            const msg =
                error?.shortMessage ||
                error?.message ||
                'Could not add network to your wallet.';
            setErrorMessage(msg);
            throw error;
        } finally {
            setIsAddPending(false);
        }
    }, [walletClient]);

    const switchToTargetChain = useCallback(async () => {
        setErrorMessage('');
        try {
            await switchChainAsync({ chainId: targetChainId });
            setNeedsAddNetwork(false);
            return;
        } catch (error) {
            if (!isChainNotAddedError(error)) {
                const msg =
                    error?.shortMessage ||
                    error?.message ||
                    'Could not switch network.';
                setErrorMessage(msg);
                throw error;
            }
            setNeedsAddNetwork(true);
        }

        try {
            await addTargetChain();
            await switchChainAsync({ chainId: targetChainId });
            setNeedsAddNetwork(false);
        } catch (error) {
            setErrorMessage(
                error?.shortMessage ||
                    error?.message ||
                    'Could not add or switch network.',
            );
            throw error;
        }
    }, [switchChainAsync, addTargetChain]);

    const isPending = isSwitchPending || isAddPending;

    return {
        switchToTargetChain,
        addTargetChain,
        needsAddNetwork,
        isPending,
        errorMessage,
        displayChainName,
        setNeedsAddNetwork,
    };
}
